import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useChatStream } from "@/lib/useChatStream";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function FloatingChat() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [convId, setConvId] = useState(null);
  const scrollRef = useRef(null);
  const { streaming, send, cancel } = useChatStream();

  useEffect(() => {
    api.get("/chat/models").then(({ data }) => { setModels(data.models); setModelId(data.default); });
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const submit = async () => {
    if (!input.trim() || streaming) return;
    const text = input;
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", pending: true }]);
    setInput("");
    await send({
      conv_id: convId, message: text, model: modelId, guest: !user,
      onMeta: (ev) => { if (ev.conv_id) setConvId(ev.conv_id); },
      onDelta: (chunk) => {
        setMessages((m) => {
          const arr = m.slice();
          const last = arr[arr.length - 1];
          if (last && last.role === "assistant") arr[arr.length - 1] = { ...last, content: (last.content || "") + chunk };
          return arr;
        });
      },
      onDone: () => setMessages((m) => m.map((x, i) => i === m.length - 1 ? { ...x, pending: false } : x)),
      onError: (e) => { toast.error(e || "Stream error"); setMessages((m) => m.filter((x) => !x.pending)); },
    });
  };

  const clear = () => { setMessages([]); setConvId(null); };

  return (
    <>
      <button
        data-testid="floating-chat-btn"
        onClick={() => setOpen(true)}
        aria-label="Open AI chat"
        className="fixed bottom-6 right-6 z-30 w-14 h-14 rounded-2xl flex items-center justify-center transition-transform hover:scale-105"
        style={{ background: "var(--grad-purple)", boxShadow: "var(--shadow-lg), var(--shadow-glow-purple)" }}
      >
        <span className="font-serif-elite text-2xl leading-none text-white">◆</span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="bg-[color:var(--bg-surface)] border-l border-[color:var(--border-default)] text-white w-full sm:max-w-md p-0 flex flex-col" data-testid="floating-chat-sheet">
          <SheetHeader className="border-b border-[color:var(--border-default)] px-4 py-3">
            <SheetTitle className="text-white font-serif-elite text-xl flex items-center justify-between">
              <span>Quick Chat</span>
              <div className="flex items-center gap-2">
                <select
                  data-testid="floating-model-select"
                  value={modelId}
                  onChange={(e) => setModelId(e.target.value)}
                  className="bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-2 py-1 text-[10px] font-mono-data text-white focus:outline-none"
                >
                  {models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
                <Link to="/chat" onClick={() => setOpen(false)} data-testid="floating-expand" className="text-[10px] font-mono-data text-[color:var(--text-secondary)] hover:text-white border border-[color:var(--border-default)] px-2 py-1">EXPAND ↗</Link>
              </div>
            </SheetTitle>
          </SheetHeader>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
            {messages.length === 0 ? (
              <div className="text-xs font-mono-data text-[color:var(--text-muted)] py-8 text-center">
                Quick market questions · powered by Anthropic Claude.<br />
                {!user && <span className="text-gold">Guest mode — history not saved.</span>}
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((m, i) => (
                  <div key={i} data-testid={`fc-msg-${m.role}-${i}`}>
                    <div className={`text-[9px] font-mono-data tracking-widest mb-0.5 ${m.role === "user" ? "text-[color:var(--text-muted)]" : "text-gold"}`}>
                      {m.role === "user" ? "YOU" : "◆ AI"}
                    </div>
                    <div className="whitespace-pre-wrap text-xs leading-relaxed font-body">
                      {m.content}
                      {m.pending && <span className="inline-block w-1 h-3 bg-white ml-1 animate-pulse align-middle" />}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-[color:var(--border-default)] p-3">
            <div className="flex gap-2">
              <textarea
                data-testid="fc-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
                placeholder="Ask anything…"
                rows={2}
                className="flex-1 bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-2 py-1.5 text-xs text-white placeholder:text-[color:var(--text-muted)] resize-none focus:outline-none focus:border-[color:var(--border-focus)] font-body"
              />
              {streaming ? (
                <button data-testid="fc-stop" onClick={cancel} className="bg-[#FF3B30] text-white px-3 text-[10px] font-mono-data hover:bg-[#e0261c]">STOP</button>
              ) : (
                <button data-testid="fc-send" onClick={submit} disabled={!input.trim()} className="bg-white text-black px-3 text-[10px] font-mono-data hover:opacity-90 disabled:opacity-40">SEND</button>
              )}
            </div>
            {messages.length > 0 && (
              <button data-testid="fc-clear" onClick={clear} className="mt-2 w-full text-[10px] font-mono-data text-[color:var(--text-muted)] hover:text-white border border-[color:var(--border-default)] py-1">CLEAR</button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
